import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPlaceById, createPlace, updatePlace } from '../services/places.service';
import { uploadImage, deleteImageByUrl } from '../services/upload.service';

// Generate slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Vercel serverless body limit is 4.5MB - keep under to avoid 413
const MAX_IMAGE_SIZE_BYTES = 4 * 1024 * 1024; // 4MB per image

// LocalStorage keys
const STORAGE_KEY_NEW = 'knowindia_place_form_new';
const STORAGE_KEY_EDIT_PREFIX = 'knowindia_place_form_edit_';

const getStorageKey = (id) => id ? `${STORAGE_KEY_EDIT_PREFIX}${id}` : STORAGE_KEY_NEW;

const saveToLocalStorage = (key, formData, uploadedImages) => {
  try {
    const dataToSave = {
      formData,
      uploadedImages,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(key, JSON.stringify(dataToSave));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const loadFromLocalStorage = (key) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error loading from localStorage:', error);
  }
  return null;
};

const clearLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

function PlaceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasRecoveredDraft, setHasRecoveredDraft] = useState(false);

  const storageKey = getStorageKey(id);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    address: '',
    city: '',
    state: '',
    status: 'published',
    map_link: '',
    images: [],
    key_information: [{ question: '', answer: '' }]
  });

  const [uploadedImages, setUploadedImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [featuredImageIndex, setFeaturedImageIndex] = useState(0);

  const fetchPlace = useCallback(async () => {
    if (!id) return;

    try {
      setIsFetching(true);
      const response = await getPlaceById(id);
      const place = response.data || response;

      const images = place.images || [];
      const processedImages = images.map(img => ({
        id: img.id,
        url: img.image_url || img,
        public_id: img.public_id || null,
        isExisting: true
      }));

      setFormData({
        name: place.name || '',
        slug: place.slug || generateSlug(place.name || ''),
        description: place.description || '',
        address: place.address || '',
        city: place.city || '',
        state: place.state || '',
        status: place.status || 'published',
        map_link: place.map_link || '',
        images: [],
        key_information: place.keyInformation?.length > 0 || place.key_information?.length > 0
          ? (place.keyInformation || place.key_information)
          : [{ question: '', answer: '' }]
      });

      setUploadedImages(processedImages);
    } catch (error) {
      console.error('Error fetching place:', error);
      toast.error('Failed to load place data');
      navigate('/places');
    } finally {
      setIsFetching(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchPlace();
    }
  }, [isEditMode, fetchPlace]);

  useEffect(() => {
    if (!isEditMode) {
      const savedData = loadFromLocalStorage(storageKey);
      if (savedData && savedData.formData) {
        const savedAt = new Date(savedData.savedAt);
        const now = new Date();
        const daysDiff = (now - savedAt) / (1000 * 60 * 60 * 24);
        
        if (daysDiff < 7) {
          setFormData(savedData.formData);
          if (savedData.uploadedImages) {
            setUploadedImages(savedData.uploadedImages);
          }
          setHasRecoveredDraft(true);
          toast.info('📝 Draft recovered! Your previous work has been restored.', {
            autoClose: 5000
          });
        } else {
          clearLocalStorage(storageKey);
        }
      }
    }
  }, [isEditMode, storageKey]);

  useEffect(() => {
    const hasContent = formData.name || formData.description || formData.city || uploadedImages.length > 0;
    
    if (hasContent) {
      saveToLocalStorage(storageKey, formData, uploadedImages);
    }
  }, [formData, uploadedImages, storageKey]);

  const clearDraft = () => {
    clearLocalStorage(storageKey);
    setHasRecoveredDraft(false);
    toast.success('Draft cleared');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      if (name === 'name' && !isEditMode) {
        newData.slug = generateSlug(value);
      }

      return newData;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImageUpload = async (e) => {
    const allFiles = Array.from(e.target.files);
    if (allFiles.length === 0) return;

    const oversized = allFiles.filter((f) => f.size > MAX_IMAGE_SIZE_BYTES);
    const files = allFiles.filter((f) => f.size <= MAX_IMAGE_SIZE_BYTES);
    if (oversized.length > 0) {
      toast.warning(
        `${oversized.length} image(s) skipped (max 4MB per file). Use smaller or compressed images.`,
        { hideProgressBar: true }
      );
    }
    if (files.length === 0) {
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    const newImages = [];

    try {
      for (const file of files) {
        const response = await uploadImage(file, `knowindia/places/${id || 'new'}`);
        if (response.success && response.data) {
          newImages.push({
            url: response.data.secure_url,
            public_id: response.data.public_id,
            isExisting: false,
            isNew: true
          });
        }
      }

      setUploadedImages(prev => [...prev, ...newImages]);
      toast.success(`${newImages.length} image(s) uploaded`, { hideProgressBar: true });
    } catch (error) {
      console.error('Error uploading images:', error);
      const is413 = error.response?.status === 413;
      toast.error(
        is413
          ? 'File too large (max 4MB per image). Use a smaller or compressed image.'
          : 'Failed to upload some images',
        { hideProgressBar: true }
      );
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (index) => {
    const image = uploadedImages[index];

    if (image.isExisting && image.url) {
      setImagesToDelete(prev => [...prev, image.url]);
    }

    if (image.isNew && image.public_id) {
      try {
        await deleteImageByUrl(image.url);
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }

    setUploadedImages(prev => prev.filter((_, i) => i !== index));

    if (featuredImageIndex === index) {
      setFeaturedImageIndex(0);
    } else if (featuredImageIndex > index) {
      setFeaturedImageIndex(prev => prev - 1);
    }
  };

  const handleKeyInfoChange = (index, field, value) => {
    const newKeyInfo = [...formData.key_information];
    newKeyInfo[index] = { ...newKeyInfo[index], [field]: value };
    setFormData(prev => ({ ...prev, key_information: newKeyInfo }));
  };

  const addKeyInfo = () => {
    setFormData(prev => ({
      ...prev,
      key_information: [{ question: '', answer: '' }, ...prev.key_information]
    }));
  };

  const removeKeyInfo = (index) => {
    setFormData(prev => ({
      ...prev,
      key_information: prev.key_information.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Place name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      const submitData = new FormData();

      submitData.append('name', formData.name);
      submitData.append('slug', formData.slug || formData.name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, ''));
      submitData.append('description', formData.description);
      submitData.append('address', formData.address);
      submitData.append('city', formData.city);
      submitData.append('map_link', formData.map_link);
      submitData.append('status', formData.status);

      if (formData.state) {
        submitData.append('state', formData.state);
      }

      const validKeyInfo = formData.key_information.filter(
        info => info.question.trim() && info.answer.trim()
      );
      submitData.append('key_information', JSON.stringify(validKeyInfo));

      const newImageUrls = uploadedImages
        .filter(img => img.isNew)
        .map(img => img.url);
      if (newImageUrls.length > 0) {
        submitData.append('image_urls', JSON.stringify(newImageUrls));
      }

      if (imagesToDelete.length > 0) {
        submitData.append('images_to_delete', JSON.stringify(imagesToDelete));
      }

      if (isEditMode) {
        await updatePlace(id, submitData);
        toast.success('Place updated successfully');
      } else {
        await createPlace(submitData);
        toast.success('Place created successfully');
      }

      clearLocalStorage(storageKey);
      navigate('/places');
    } catch (error) {
      console.error('Error saving place:', error);
      console.error('Error response:', error.response?.data);
      
      const serverError = error.response?.data?.error;
      const serverMessage = error.response?.data?.message;
      const errorMessage = serverMessage || serverError || 'Failed to save place';

      if (error.response?.data?.errors) {
        const apiErrors = {};
        error.response.data.errors.forEach(err => {
          apiErrors[err.field] = err.message;
        });
        setErrors(apiErrors);
      }

      if (serverError && serverError !== serverMessage) {
        console.error('Server error details:', serverError);
        toast.error(`${errorMessage}: ${serverError}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-indigo-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-12">
      
      {/* ===== HEADER ===== */}
      <div className="mb-8">
        <Link
          to="/places"
          className="inline-flex items-center gap-2 text-white/40 hover:text-purple-400 mb-4 transition-colors"
        >
          <span className="material-symbols-outlined text-lg">arrow_back</span>
          Back to Places
        </Link>

        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isEditMode ? 'bg-gradient-to-br from-amber-600 to-orange-600' : 'bg-gradient-to-br from-purple-600 to-indigo-600'}`}>
            <span className="material-symbols-outlined text-2xl text-white">
              {isEditMode ? 'edit' : 'add_location'}
            </span>
          </div>
          <div>
            <h1 className="text-3xl font-extralight tracking-tight text-white">
              {isEditMode ? 'Edit Place' : 'Add New Place'}<span className="text-purple-400 font-bold">.</span>
            </h1>
            <p className="text-sm text-white/40 mt-1">
              {isEditMode ? 'Update the destination details' : 'Fill in the details to add a new destination'}
            </p>
          </div>
        </div>
      </div>

      {/* ===== DRAFT RECOVERED BANNER ===== */}
      {hasRecoveredDraft && (
        <div className="mb-6 bg-purple-500/10 border border-purple-500/20 rounded-2xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-purple-400">description</span>
            </div>
            <div>
              <p className="font-medium text-white">Draft Recovered</p>
              <p className="text-sm text-white/50">Your previous work has been restored. Data auto-saves as you type.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={clearDraft}
            className="px-4 py-2 text-sm font-medium text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg transition-colors"
          >
            Clear Draft
          </button>
        </div>
      )}

      {/* ===== AUTO-SAVE INDICATOR ===== */}
      <div className="mb-4 flex items-center gap-2 text-sm text-white/40">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        <span>Auto-saving to browser storage</span>
      </div>

      {/* ===== FORM ===== */}
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Basic Information */}
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5 flex items-center gap-2">
            <span className="material-symbols-outlined text-purple-400">info</span>
            <h2 className="text-lg font-medium text-white">Basic Information</h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">
                  Place Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Taj Mahal"
                  className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white placeholder:text-white/30 focus:outline-none transition-all ${errors.name ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-purple-500/50'}`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">
                Description <span className="text-red-400">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                placeholder="Describe this destination..."
                className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white placeholder:text-white/30 focus:outline-none resize-none transition-all ${errors.description ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-purple-500/50'}`}
              />
              {errors.description && <p className="mt-1 text-sm text-red-400">{errors.description}</p>}
            </div>
          </div>
        </div>

        {/* Location Details */}
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5 flex items-center gap-2">
            <span className="material-symbols-outlined text-purple-400">location_on</span>
            <h2 className="text-lg font-medium text-white">Location Details</h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Full address"
                className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 transition-all"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">
                  City <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="e.g., Agra"
                  className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white placeholder:text-white/30 focus:outline-none transition-all ${errors.city ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-purple-500/50'}`}
                />
                {errors.city && <p className="mt-1 text-sm text-red-400">{errors.city}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="e.g., Uttar Pradesh"
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">Google Maps Embed URL</label>
              <input
                type="url"
                name="map_link"
                value={formData.map_link}
                onChange={handleChange}
                placeholder="https://www.google.com/maps/embed?..."
                className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 transition-all"
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-purple-400">image</span>
                <h2 className="text-lg font-medium text-white">Images</h2>
              </div>
              <p className="text-sm text-white/40 mt-1">Upload images for this place</p>
            </div>
            {isUploading && (
              <div className="flex items-center gap-2 text-purple-400">
                <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                <span className="text-sm">Uploading...</span>
              </div>
            )}
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {uploadedImages.map((image, index) => (
                <div
                  key={index}
                  className={`relative group aspect-square rounded-xl overflow-hidden bg-white/5 ${
                    index === featuredImageIndex ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-[#0a0a0f]' : ''
                  }`}
                >
                  <img
                    src={image.url}
                    alt={`Place ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Featured badge */}
                  {index === featuredImageIndex && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-purple-500 text-white text-xs rounded-lg flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">star</span>
                      Featured
                    </div>
                  )}

                  {/* Actions overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    {index !== featuredImageIndex && (
                      <button
                        type="button"
                        onClick={() => setFeaturedImageIndex(index)}
                        className="p-2 rounded-lg bg-white/10 backdrop-blur-sm text-white hover:bg-purple-500 transition-colors"
                        title="Set as featured"
                      >
                        <span className="material-symbols-outlined text-lg">star</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="p-2 rounded-lg bg-white/10 backdrop-blur-sm text-white hover:bg-red-500 transition-colors"
                      title="Remove image"
                    >
                      <span className="material-symbols-outlined text-lg">delete</span>
                    </button>
                  </div>
                </div>
              ))}

              {/* Upload button */}
              <label className={`aspect-square rounded-xl border-2 border-dashed transition-colors cursor-pointer flex flex-col items-center justify-center gap-2 ${
                isUploading
                  ? 'border-white/10 bg-white/[0.02] cursor-not-allowed'
                  : 'border-white/20 hover:border-purple-500/50 bg-white/[0.02] hover:bg-purple-500/5'
              }`}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  className="hidden"
                />
                {isUploading ? (
                  <span className="material-symbols-outlined text-3xl text-white/30 animate-spin">progress_activity</span>
                ) : (
                  <span className="material-symbols-outlined text-3xl text-white/30">cloud_upload</span>
                )}
                <span className="text-sm font-medium text-white/40">
                  {isUploading ? 'Uploading...' : 'Add Images'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Key Information */}
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-purple-400">help</span>
                <h2 className="text-lg font-medium text-white">Key Information</h2>
              </div>
              <p className="text-sm text-white/40 mt-1">Add Q&A style information</p>
            </div>
            <button
              type="button"
              onClick={addKeyInfo}
              className="px-3 py-1.5 bg-white/5 border border-white/10 text-white rounded-lg text-sm font-medium hover:bg-white/10 transition-colors flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              Add
            </button>
          </div>
          <div className="p-6 space-y-4">
            {formData.key_information.map((keyInfo, index) => (
              <div key={index} className="bg-white/[0.02] border border-white/5 rounded-xl p-4 relative">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-2">Question</label>
                    <input
                      type="text"
                      value={keyInfo.question}
                      onChange={(e) => handleKeyInfoChange(index, 'question', e.target.value)}
                      placeholder="e.g., What are the visiting hours?"
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-2">Answer</label>
                    <input
                      type="text"
                      value={keyInfo.answer}
                      onChange={(e) => handleKeyInfoChange(index, 'answer', e.target.value)}
                      placeholder="e.g., 6:00 AM to 6:00 PM"
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 transition-all"
                    />
                  </div>
                </div>

                {formData.key_information.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeKeyInfo(index)}
                    className="absolute top-3 right-3 p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">close</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/places')}
            disabled={isLoading}
            className="px-6 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isUploading}
            className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl font-medium transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-purple-500/25"
          >
            {isLoading ? (
              <>
                <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditMode ? 'Update Place' : 'Create Place'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PlaceForm;
